/**
 * Caredove Embed Widget v2
 * Entry point for the widget
 */

import { Widget } from '@/core/Widget';
import { ConfigParser } from '@/core/ConfigParser';
import { UrlBuilder } from '@/core/UrlBuilder';
import { Sanitizer } from '@/security/Sanitizer';
import { OriginValidator } from '@/security/OriginValidator';

// Import styles
import './styles/theme.css';
import './styles/button.css';
import './styles/modal.css';
import './styles/spinner.css';
import './styles/iframe.css';

// Export public API
export { Widget, ConfigParser, UrlBuilder, Sanitizer, OriginValidator };

// Export types
export type { WidgetConfig, WidgetMode, ContentType, Environment } from '@/types/config';
export type {
  IncomingPostMessage,
  OutgoingPostMessage,
  PostMessageType
} from '@/types/messages';

/**
 * Widget version
 */
export const VERSION = '2.0.0';

/**
 * Store all widget instances for cleanup
 */
const widgetInstances: Widget[] = [];

/**
 * Auto-load CSS stylesheet
 * Mimics v1 behavior where CSS is loaded dynamically
 */
function loadCss(): void {
  const cssId = 'caredove-embed-v2-css';

  // Check if CSS is already loaded
  if (document.getElementById(cssId)) {
    return;
  }

  // Get the script URL to determine the base path
  const currentScript = document.currentScript as HTMLScriptElement | null;
  let cssUrl = '';

  if (currentScript && currentScript.src) {
    // Replace embed-v2.js with embed-v2.css
    cssUrl = currentScript.src.replace(/embed-v2\.js$/, 'embed-v2.css');
  } else {
    // Fallback: Try to find the script tag
    const scripts = document.querySelectorAll('script[src*="embed-v2.js"]');
    if (scripts.length > 0) {
      const scriptSrc = (scripts[0] as HTMLScriptElement).src;
      cssUrl = scriptSrc.replace(/embed-v2\.js$/, 'embed-v2.css');
    } else {
      console.error('[CaredoveWidget] Could not determine CSS URL. Please manually add <link rel="stylesheet" href="...embed-v2.css">');
      return;
    }
  }

  // Create and append link tag
  const link = document.createElement('link');
  link.id = cssId;
  link.rel = 'stylesheet';
  link.type = 'text/css';
  link.href = cssUrl;

  // Insert before any existing style tags, or append to head
  const firstStyle = document.head.querySelector('style');
  if (firstStyle) {
    document.head.insertBefore(link, firstStyle);
  } else {
    document.head.appendChild(link);
  }

  console.log('[CaredoveWidget] CSS loaded:', cssUrl);
}

/**
 * Initialize all widgets on the page
 */
function initializeWidgets(): void {
  console.log('[CaredoveWidget] v2.0.0 initializing...');

  // Load CSS first
  loadCss();

  // Find all buttons with cd--content-id attribute
  const buttons = document.querySelectorAll<HTMLButtonElement>('button[cd--content-id]');

  if (buttons.length === 0) {
    console.warn('[CaredoveWidget] No buttons found with cd--content-id attribute');
    return;
  }

  console.log(`[CaredoveWidget] Found ${buttons.length} widget button(s)`);

  // Initialize each widget
  buttons.forEach((button, index) => {
    try {
      const widget = new Widget(button);
      widget.initialize();
      widgetInstances.push(widget);

      console.log(`[CaredoveWidget] Widget ${index + 1} initialized successfully`);
    } catch (error) {
      console.error(`[CaredoveWidget] Error initializing widget ${index + 1}:`, error);
    }
  });
}

/**
 * Cleanup all widgets
 */
function cleanupWidgets(): void {
  widgetInstances.forEach(widget => {
    widget.destroy();
  });
  widgetInstances.length = 0;
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeWidgets);
} else {
  // DOM is already loaded
  initializeWidgets();
}

// Cleanup on page unload
window.addEventListener('beforeunload', cleanupWidgets);

// Make widget available globally for debugging
declare global {
  interface Window {
    CaredoveEmbed: {
      VERSION: string;
      Widget: typeof Widget;
      ConfigParser: typeof ConfigParser;
      UrlBuilder: typeof UrlBuilder;
      Sanitizer: typeof Sanitizer;
      OriginValidator: typeof OriginValidator;
      instances: Widget[];
      reinitialize: () => void;
      cleanup: () => void;
    };
  }
}

window.CaredoveEmbed = {
  VERSION,
  Widget,
  ConfigParser,
  UrlBuilder,
  Sanitizer,
  OriginValidator,
  instances: widgetInstances,
  reinitialize: initializeWidgets,
  cleanup: cleanupWidgets,
};
